import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Typography, LinearProgress, Avatar } from "@mui/material";

import { ActionButton, CardLayout, DeleteModal, InputField } from "../v3";
import { AddEditTask } from "./AddEditTask.component";
import { ProjectService, TaskService } from "../../services";
import { DELETE_TASK, EDIT_TASK } from "../../constants";
import { useHasPermission } from "../../utils";

export const TaskDetails = ({ taskData = {}, taskProgressQuantity = 0, reloadData }) => {
  const [open, setOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    taskId: null,
  });
  const [taskDetails, setTaskDetails] = useState({
    status: undefined,
    quantity: undefined,
    unitOfMeasure: undefined,
    project: undefined,
    stage: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const { id } = useParams();
  const navigate = useNavigate();

  const hasTaskEdit = useHasPermission(EDIT_TASK);
  const hasTaskDelete = useHasPermission(DELETE_TASK);

  useEffect(() => {
    const totalAddedQuantity = taskData.taskProgresses
      ? taskData.taskProgresses.reduce((sum, progress) => sum + progress.quantity, 0)
      : 0;

    setTaskDetails({
      status: taskData?.status || undefined,
      quantity: taskData?.quantity || undefined,
      unitOfMeasure: taskData?.unitOfMeasure || undefined,
      project: taskData?.project || undefined,
      startDate: taskData?.startDate || undefined,
      endDate: taskData?.endDate || undefined,
      addedQuantity: totalAddedQuantity,
    });

    if (taskData?.projectStage?.stageId) fetchData(taskData?.projectStage?.stageId);
  }, [taskData]);

  const fetchData = async (stageId) => {
    try {
      const { data: stageData } = await ProjectService.getStageById(stageId);
      setTaskDetails((prev) => ({ ...prev, stage: stageData || null }));
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setTaskDetails({ ...taskDetails, [name]: value });
  };

  const handleEditTask = async () => setOpen(true);

  const openDeleteModal = () => setDeleteModal({ isOpen: true, taskId: id });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false, taskId: null });

  const handleDelete = async () => {
    try {
      await TaskService.deleteTask(deleteModal.taskId);
      closeDeleteModal();
      toast.success("Task deleted successfully");
      navigate(-1);
    } catch (error) {
      toast.error("Failed to delete task");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const statusOptions = [
    { label: "To Do", value: "TODO" },
    { label: "In Progress", value: "INPROGRESS" },
    { label: "On Hold", value: "ON_HOLD" },
    { label: "Done", value: "DONE" },
    { label: "Cancelled", value: "CANCELLED" },
  ];

  const progress = taskDetails.quantity ? Math.min((taskDetails.addedQuantity / taskDetails.quantity) * 100, 100) : 0;

  return (
    <>
      <CardLayout sx={{ padding: "14px", display: "flex", flexDirection: "column" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Task Details
        </Typography>
        <Box
          sx={{
            backgroundColor: "#F6F6F6",
            flex: 1,
            padding: "14px",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {/* Assignee and Status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: "14px",
                borderBottom: "1px solid #E5E5E5",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    marginRight: "16px",
                  }}
                >
                  <Avatar
                    src={taskData?.imageUrl}
                    alt="Profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      backgroundColor: "#F2ECF9",
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Assignee
                  </Typography>
                  <Typography variant="caption" display="block">
                    {taskData?.assignedToUser
                      ? `${taskData.assignedToUser.firstName} ${taskData.assignedToUser.lastName}`
                      : "Not Assigned"}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <InputField
                  disabled={true}
                  select={true}
                  selectOptions={statusOptions}
                  label="Status"
                  name="status"
                  value={taskDetails?.status}
                  onChange={updateDetailsState}
                  style={{
                    width: 200,
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#583AF3",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#583AF3",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Quantity and Dates */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 0px",
                borderBottom: "1px solid #E5E5E5",
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Project:{" "}
                  <Typography variant="caption" component="span" sx={{ fontWeight: 400 }}>
                    {taskDetails?.project?.name ?? "Not Assigned"}
                  </Typography>
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginTop: "4px" }}>
                  Stage:{" "}
                  <Typography variant="caption" component="span" sx={{ fontWeight: 400 }}>
                    {taskDetails?.stage?.name ?? "Not Assigned"}
                  </Typography>
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Start date:{" "}
                  <Typography variant="caption" component="span" sx={{ fontWeight: 400 }}>
                    {taskDetails?.startDate ?? "dd/mm/yyyy"}
                  </Typography>
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginTop: "4px" }}>
                  End date:{" "}
                  <Typography variant="caption" component="span" sx={{ fontWeight: 400 }}>
                    {taskDetails?.endDate ?? "dd/mm/yyyy"}
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* Progress Section */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Progress
              </Typography>
              <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                {`${taskDetails.addedQuantity ?? 0} of ${taskDetails.quantity ?? 0} ${taskDetails.unitOfMeasure ?? ""}`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#a79ae7ff",
                  "& .MuiLinearProgress-bar": {
                    background: "#674BF5",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Bottom Buttons */}
          {(hasTaskEdit || hasTaskDelete) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
              {hasTaskDelete && (
                <ActionButton
                  buttonText="Delete"
                  variant="outlined"
                  onButtonClick={openDeleteModal}
                  sx={{
                    textTransform: "capitalize",
                  }}
                />
              )}
              {hasTaskEdit && (
                <ActionButton
                  buttonText="Edit"
                  onButtonClick={handleEditTask}
                  sx={{
                    backgroundColor: "#674BF5",
                    textTransform: "capitalize",
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </CardLayout>

      {/* Edit Modal */}
      <AddEditTask
        open={open}
        onClose={() => setOpen(false)}
        taskId={id}
        taskProgressQuantity={taskProgressQuantity}
        onSuccess={reloadData}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this task?"
      />
    </>
  );
};
